<template>
  <v-app :class="{ 'dark-mode': $vuetify.theme.dark }">
    <SidebarNew
      :drawer.sync="drawer"
      :mini.sync="miniVariant"
      @toggle-dark-mode="toggleDarkMode"
    />

    <v-app-bar
      app
      flat
      :color="$vuetify.theme.dark ? 'grey darken-4' : 'white'"
      height="64"
      :elevation="$vuetify.theme.dark ? 0 : 1"
    >
      <v-app-bar-nav-icon
        @click.stop="toggleDrawer"
        class="hidden-lg-and-up"
      />

      <v-btn
        icon
        @click.stop="toggleMini"
        class="hidden-md-and-down ml-2"
      >
        <v-icon>{{ miniVariant ? 'mdi-menu' : 'mdi-menu-open' }}</v-icon>
      </v-btn>

      <v-toolbar-title class="ml-2 font-weight-medium">
        {{ $route.meta.title || 'Salamanda ERP' }}
      </v-toolbar-title>

      <v-spacer />

      <!-- Language Selector -->
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="mr-2"
          >
            <v-icon>mdi-translate</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item
            v-for="lang in languages"
            :key="lang.code"
            @click="changeLang(lang.code)"
            :class="{ 'primary--text': $i18n.locale === lang.code }"
          >
            <v-list-item-avatar size="24">
              <v-img :src="`${publicPath}assets/img/lang/${lang.code}.png`" />
            </v-list-item-avatar>
            <v-list-item-title>{{ lang.name }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- License Info -->
      <v-chip
        :color="licenseColor"
        text-color="white"
        class="mr-3"
        small
      >
        <v-icon small left>mdi-license</v-icon>
        {{ license.client }}
        <span v-if="elc === 0" class="ml-1">(expires soon)</span>
        <span v-else class="ml-1">({{ license.end_date | date(true) }})</span>
      </v-chip>

      <!-- User Menu -->
      <v-menu
        offset-y
        left
        bottom
        transition="slide-y-transition"
        :close-on-content-click="false"
        max-width="220"
        content-class="user-dropdown-menu"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="user-avatar-btn"
          >
            <v-avatar
              size="36"
              :color="$vuetify.theme.dark ? 'primary' : 'primary lighten-1'"
            >
              <v-img v-if="user && user.photo" :src="user.photo"></v-img>
              <span v-else class="white--text">
                {{ user && user.name ? user.name.charAt(0).toUpperCase() : '' }}
              </span>
            </v-avatar>
          </v-btn>
        </template>
        <v-card class="user-dropdown-card">
          <!-- User Info Section -->
          <v-list-item class="user-info-section pa-2">
            <v-list-item-avatar>
              <v-avatar
                size="40"
                :color="$vuetify.theme.dark ? 'primary' : 'primary lighten-1'"
              >
                <v-img v-if="user && user.photo" :src="user.photo"></v-img>
                <span v-else class="white--text">
                  {{ user && user.name ? user.name.charAt(0).toUpperCase() : '' }}
                </span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="subtitle-1 font-weight-medium">
                {{ user && user.name ? user.name : 'User' }}
              </v-list-item-title>
              <v-list-item-subtitle class="caption">
                {{ user && user.username ? user.username : '' }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-divider class="my-0"></v-divider>

          <!-- Menu Options -->
          <v-list dense class="py-0">
            <v-list-item @click="meta.passwordDialog = true" class="user-menu-item">
              <v-list-item-icon class="mr-2">
                <v-icon small>mdi-key-variant</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="body-2">{{ $t('link.pw_reset') }}</v-list-item-title>
            </v-list-item>

            <v-list-item :to="{ name: 'config_org' }" class="user-menu-item">
              <v-list-item-icon class="mr-2">
                <v-icon small>mdi-cog</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="body-2">{{ $t('setting.name') }}</v-list-item-title>
            </v-list-item>

            <v-divider class="my-0"></v-divider>

            <v-list-item @click="$authService.logout()" class="user-menu-item error--text">
              <v-list-item-icon class="mr-2">
                <v-icon small color="error">mdi-logout</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="body-2">{{ $t('link.logout') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
    </v-app-bar>

    <v-main>
      <v-container fluid class="pa-1">
        <transition name="fade-transform" mode="out-in">
          <router-view />
        </transition>
      </v-container>
    </v-main>

    <!-- Password Dialog -->
    <v-dialog
      v-model="meta.passwordDialog"
      max-width="500"
      persistent
    >
      <v-card>
        <v-card-title class="headline">
          <v-icon left>mdi-lock-reset</v-icon>
          Update Password
        </v-card-title>
        <v-divider />
        <v-form ref="passForm" v-model="meta.valid" @submit.prevent="updatePassword">
          <v-card-text>
            <v-text-field
              v-model="pass.old_password"
              label="Current Password"
              type="password"
              :rules="[v => !!v || 'Current password is required']"
              prepend-icon="mdi-lock-outline"
              outlined
              dense
            />
            <v-text-field
              v-model="pass.password"
              label="New Password"
              type="password"
              :rules="[v => !!v || 'New password is required']"
              prepend-icon="mdi-lock"
              outlined
              dense
            />
            <v-text-field
              v-model="pass.password_confirmation"
              label="Confirm Password"
              type="password"
              :rules="[
                v => !!v || 'Password confirmation is required',
                v => v === pass.password || 'Passwords must match'
              ]"
              prepend-icon="mdi-lock-check"
              outlined
              dense
            />
          </v-card-text>
        </v-form>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="meta.passwordDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="meta.loading"
            :disabled="!meta.valid"
            @click="updatePassword"
          >
            <v-icon left>mdi-check</v-icon>
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import SidebarNew from '../_Partials/SidebarNew'
import { mapState } from 'vuex'

export default {
  name: 'DashboardNew',
  components: { SidebarNew },
  data () {
    return {
      drawer: true,
      miniVariant: false,
      languages: [
        { code: 'en', name: 'English' },
        { code: 'fr', name: 'Français' }
      ],
      pass: {
        old_password: '',
        password: '',
        password_confirmation: ''
      },
      meta: {
        loading: false,
        valid: false,
        passwordDialog: false
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.Auth.user,
      license: state => state.Auth.license
    }),
    evaluateLicenseColor () {
      const diff = this.$moment(this.license.end_date).diff(this.$moment(new Date()), 'days')
      if (diff > 90) {
        return 1
      } else if (diff > 60) return 0
      return -1
    },
    elc () {
      return this.evaluateLicenseColor
    },
    licenseColor () {
      if (this.elc === 1) return 'success'
      if (this.elc === 0) return 'warning'
      return 'error'
    }
  },
  watch: {
    '$vuetify.breakpoint.mdAndDown' (val) {
      if (val) {
        this.drawer = false
      } else {
        this.drawer = true
      }
    }
  },
  methods: {
    toggleDrawer () {
      this.drawer = !this.drawer
    },
    toggleMini () {
      this.miniVariant = !this.miniVariant
      localStorage.setItem('sidebarMini', this.miniVariant)
    },
    toggleDarkMode () {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark
      localStorage.setItem('darkMode', this.$vuetify.theme.dark)
    },
    updatePassword () {
      if (this.$refs.passForm.validate()) {
        this.meta.loading = true
        this.$authService.changePassword(this.pass)
          .then(data => {
            this.displayMessage('Password Updated', () => {
              this.$authService.logout()
            })
          })
          .catch(error => {
            if (error.response) {
              if (error.response.status === 403) {
                this.displayMessage(error.response.data.message, () => {
                }, 'error')
              } else if (error.response.status !== 422) {
                this.notify(error.message, 'Error', 'error')
              }
            }
          })
          .finally(() => {
            this.meta.loading = false
          })
      }
    },
    changeLang (lang) {
      this.setLanguage(lang)
    },
    loadSavedPreferences () {
      // Load dark mode preference
      const savedDarkMode = localStorage.getItem('darkMode')
      if (savedDarkMode !== null) {
        this.$vuetify.theme.dark = savedDarkMode === 'true'
      }

      // Load mini variant preference
      const savedMini = localStorage.getItem('sidebarMini')
      if (savedMini !== null) {
        this.miniVariant = savedMini === 'true'
      }
    }
  },
  mounted () {
    this.loadSavedPreferences()

    // Set drawer based on screen size
    if (this.$vuetify.breakpoint.mdAndDown) {
      this.drawer = false
    }

    // Fetch license if not loaded
    if (!Object.keys(this.license).length) {
      this.$store.dispatch('Auth/fetchLicense')
        .catch(() => {
          this.$router.push({ name: 'base' })
        })
    }
  }
}
</script>

<style scoped>
.fade-transform-enter-active,
.fade-transform-leave-active {
  transition: all 0.3s;
}

.fade-transform-enter {
  opacity: 0;
  transform: translateX(-30px);
}

.fade-transform-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.dark-mode {
  background-color: #121212;
}

/* User Dropdown Menu Styles */
.user-dropdown-menu {
  border-radius: 8px;
  overflow: hidden;
}

.user-dropdown-card {
  border-radius: 8px;
  overflow: hidden;
}

.user-avatar-btn {
  transition: transform 0.2s;
}

.user-avatar-btn:hover {
  transform: scale(1.05);
}

.user-info-section {
  background-color: rgba(0, 0, 0, 0.02);
}

.theme--dark .user-info-section {
  background-color: rgba(255, 255, 255, 0.05);
}

.user-menu-item {
  min-height: 36px !important;
  padding: 0 12px !important;
  transition: background-color 0.2s;
}

.user-menu-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.theme--dark .user-menu-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
}
</style>
